import {useEffect, useState} from "react";
import {ArrowLeft, Save, Send, Activity, FileText, Settings, Store, Mail} from "lucide-react";
import ProductsAdmin from "@/pages/ProductsAdmin";
import axios from "axios";
import {API, apiGet, apiPost, apiPut} from "@/lib/api";
import {SITE_DEFAULTS} from "@/lib/site";
import Admin from "@/pages/Admin";
import AuthModal from "@/pages/modals/Auth";

const TEXT_KEYS = Object.keys(SITE_DEFAULTS).filter(k => typeof SITE_DEFAULTS[k] === "string");
const TOGGLES = Object.keys(SITE_DEFAULTS).filter(k => typeof SITE_DEFAULTS[k] === "boolean");
const label = (k) => k.replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());

function PageText({notify}) {
  const [c, setC] = useState(null);
  useEffect(() => { apiGet("/site-content").then(d => setC({...SITE_DEFAULTS, ...d.content})); }, []);
  if (!c) return null;
  const save = async () => {
    try { await apiPut("/admin/site-content", {content: c}); notify("Saved — every user's app refreshes within a minute"); }
    catch { notify("Could not save"); }
  };
  return (
    <section className="admin-panel wide" data-testid="cp-page-text">
      <div className="admin-panel-head"><div><div className="eyebrow">FRONTEND CONTENT</div><h2>Page text & sections</h2></div>
        <button data-testid="cp-save-text" className="coral-button sm" onClick={save}><Save size={13}/> Save & publish</button></div>
      <p className="admin-help">Wrap words in *asterisks* to show them in the accent italic style. Leave a field empty to use the default.</p>
      <div className="cp-toggles">
        {TOGGLES.map(k => (
          <label key={k} className="pref-row check"><input type="checkbox" data-testid={`cp-${k}`} checked={c[k] !== false} onChange={e => setC({...c, [k]: e.target.checked})}/> {label(k).replace("Show ", "Show on home: ")}</label>
        ))}
      </div>
      <div className="cp-fields">
        {TEXT_KEYS.map(k => (
          <label key={k}>{label(k)}
            <textarea data-testid={`cp-${k}`} rows={k.includes("sub") || k.includes("purpose") ? 3 : 2} value={c[k]} onChange={e => setC({...c, [k]: e.target.value})}/>
          </label>
        ))}
      </div>
    </section>
  );
}

function Broadcast({notify}) {
  const [f, setF] = useState({title: "", body: ""});
  const send = async (e) => {
    e.preventDefault();
    try { const r = await apiPost("/admin/broadcast", f); notify(`Sent to ${r.sent_to} members`); setF({title: "", body: ""}); }
    catch { notify("Could not send"); }
  };
  return (
    <form className="admin-panel wide cp-broadcast" onSubmit={send} data-testid="cp-broadcast">
      <div className="eyebrow">NOTIFY EVERYONE</div><h2>Send an update to all members</h2>
      <p className="admin-help">Shows in every member's bell, sends a phone alert to those who turned alerts on, and refreshes everyone's app.</p>
      <input data-testid="cp-broadcast-title" required minLength={2} value={f.title} onChange={e => setF({...f, title: e.target.value})} placeholder="Title, e.g. Navratri passes are live"/>
      <textarea data-testid="cp-broadcast-body" required minLength={2} value={f.body} onChange={e => setF({...f, body: e.target.value})} placeholder="Message"/>
      <button data-testid="cp-broadcast-send" className="coral-button sm" type="submit"><Send size={13}/> Send to everyone</button>
    </form>
  );
}

function EmailLog({notify}) {
  const [d, setD] = useState(null);
  const load = () => apiGet("/admin/alerts").then(setD).catch(() => {});
  useEffect(() => { load(); }, []);
  const test = async () => {
    try { const r = await apiPost("/admin/alerts/test", {}); notify(r.sent ? `Test email sent to ${d.to}` : `Not sent: ${r.reason}`); load(); }
    catch { notify("Could not send"); }
  };
  if (!d) return null;
  return (
    <section className="admin-panel wide" data-testid="cp-emails">
      <div className="admin-panel-head"><div><div className="eyebrow">ADMIN EMAILS</div><h2>Every enquiry & approval → {d.to}</h2></div>
        <button data-testid="cp-email-test" className="coral-button sm" onClick={test}><Mail size={13}/> Send test email</button></div>
      <p className="admin-help">Concierge enquiries (members and visitors), Purchase Decision Reports, product finder requests, product suggestions, samaj approvals and join requests, public event approvals and content reports all email this address. {d.configured ? "" : "Email is not configured."}</p>
      <div className="cp-log">
        {d.items.map((i, k) => (
          <div key={k} className="cp-log-row" data-testid={`cp-email-row-${k}`}><small>{new Date(i.created_at).toLocaleString()}</small><strong>{i.email?.sent ? "Delivered to mail service" : `Not sent (${i.email?.reason || "unknown"})`}</strong><span>{i.title}</span></div>
        ))}
      </div>
    </section>
  );
}

function ActivityLog() {
  const [d, setD] = useState({items: [], total: 0, users: 0});
  useEffect(() => { apiGet("/admin/activity", {limit: 200}).then(setD).catch(() => {}); }, []);
  return (
    <section className="admin-panel wide" data-testid="cp-activity">
      <div className="eyebrow">ACTIVITY LOG</div><h2>{d.total} actions · {d.users} members</h2>
      <div className="cp-log">
        {d.items.map((i, k) => (
          <div key={k} className="cp-log-row"><small>{new Date(i.created_at).toLocaleString()}</small><strong>{i.user_name}</strong><span>{i.action}{i.meta?.page ? ` · ${i.meta.page}` : ""}{i.meta?.status ? ` · ${i.meta.status}` : ""}</span></div>
        ))}
      </div>
    </section>
  );
}

export default function ControlPanel() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("text");
  const [toast, setToast] = useState("");
  const notify = (m) => { setToast(m); setTimeout(() => setToast(""), 2800); };
  useEffect(() => { axios.get(`${API}/auth/me`, {withCredentials: true}).then(r => setUser(r.data)).catch(() => setUser(null)); }, []);

  if (user === undefined) return null;
  if (!user) return <div className="welcome"><section className="welcome-auth"><AuthModal inline close={() => {}} setUser={setUser} notify={notify}/></section></div>;
  if (!user.is_admin) return <div className="cp-denied" data-testid="cp-denied"><h2>Admins only</h2><a href="/">← Back to Indian Ocean</a></div>;

  const TABS = [["text", "Page text", FileText], ["products", "Bazaar products", Store], ["notify", "Notify everyone", Send], ["emails", "Admin emails", Mail], ["activity", "Activity log", Activity], ["tools", "Admin tools", Settings]];
  return (
    <div className="control-panel" data-testid="control-panel">
      <header className="cp-head">
        <a href="/" data-testid="cp-back" className="back-button"><ArrowLeft size={16}/> Back to app</a>
        <div><div className="eyebrow">INDIAN OCEAN · CONTROL PANEL</div><h1>Manage your platform</h1></div>
        <small>{user.email}{user.is_super_admin ? " · Super admin" : ""}</small>
      </header>
      <div className="activity-tabs">
        {TABS.map(([id, l, Icon]) => <button key={id} data-testid={`cp-tab-${id}`} className={tab === id ? "activity-tab active" : "activity-tab"} onClick={() => setTab(id)}><Icon size={13}/> {l}</button>)}
      </div>
      {tab === "text" && <PageText notify={notify}/>}
      {tab === "products" && <ProductsAdmin notify={notify}/>}
      {tab === "notify" && <Broadcast notify={notify}/>}
      {tab === "activity" && <ActivityLog/>}
      {tab === "emails" && <EmailLog notify={notify}/>}
      {tab === "tools" && <Admin notify={notify}/>}
      {toast && <div className="toast" data-testid="toast-message">{toast}</div>}
    </div>
  );
}
